/**
 * SECURE match-code screen — the physical trust-anchor step of onboarding, per the `sMatch`
 * screen in the onboarding prototype
 * (docs/superpowers/design-reference/1815-onboarding/onboarding-prototype.dc.html, lines
 * 268-292, id `#cn-match`... section): an Eyebrow reading "Secure", the serif heading "Check
 * the code on the node." (line 274), the six-digit match code rendered large and grouped
 * (`role="img"`, mono, tracked, line 275-277), and — while waiting — the body copy explaining
 * the node shows the same digits and a dashed "WAITING FOR THE NODE'S BUTTON" status row
 * (lines 278-283), or — once confirmed — a solid "CONFIRMED ON THE NODE" status row (lines
 * 285-289).
 *
 * The prototype only models `wait`/`ok`. This element additionally models a `mismatch`
 * status: the code shown here did NOT match what the node displayed, which means the
 * connection may be to the wrong device or under attack. That is a blocking security error
 * (`role="alert"`) with no way to proceed past it — only to restart the pairing/bootstrap
 * flow from scratch. There must never be a "continue anyway" escape hatch rendered from this
 * state; the whole point of the match-code step is that a mismatch cannot be waved through.
 *
 * Pure presentation: it owns no confirmation state of its own, it only renders whatever
 * `MatchcodeState` the kernel-driven flow hands it and emits `confirm-matchcode` /
 * `report-mismatch` (from `wait`) or `restart` (from `mismatch`) for the host to act on.
 *
 * Mirrors `./identify-identity.ts` and `../../pairing/pairing-view.ts`'s idiom: `extends
 * HTMLElement`, `set state()` -> `#render()`, shadow root + inline `<style>` using
 * `var(--token, <light-literal-fallback>)` so the shell's token sheet (adopted at the
 * `<node-app-shell>` root) drives theming while still standing alone if this element is ever
 * mounted outside that tree, plus the same reduced-motion/forced-colors guards. Not
 * self-registered here (like the mirrored files) — the host and the test file each register
 * the tag with a `customElements.get` guard.
 *
 * SECURITY (docs/development/CLIENT_NODE_CONVENTIONS.md §13): the match code is never
 * console'd or logged by this element — it only ever appears in the rendered DOM text, which
 * is exactly what the user is meant to read aloud/compare against the node's own screen.
 */
export class NodeOnboardingSecureMatchcodeElement extends HTMLElement {
    #state = { code: "", status: "wait" };
    set state(value) {
        this.#state = value;
        this.#render();
    }
    get state() {
        return this.#state;
    }
    connectedCallback() {
        this.#render();
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        style.textContent = `
      :host { display: block; font-family: var(--ui-font, Inter, system-ui, sans-serif); color: var(--ink-hi, #0A0A0A); }
      section {
        display: flex; flex-direction: column; gap: 16px;
        padding: clamp(20px, 6vw, 36px) clamp(16px, 5vw, 32px);
        background: var(--bg-raised, #FFFFFF);
        border: 1px solid var(--ui-line-soft, rgba(0, 0, 0, 0.06));
        border-radius: var(--ui-radius-lg, 14px);
        box-shadow: var(--ui-shadow-1, 0 1px 1px rgba(10, 10, 10, 0.05), 0 10px 24px -14px rgba(10, 10, 10, 0.25));
      }
      .eyebrow {
        font: 600 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888);
      }
      h1 {
        margin: 0; font: 400 clamp(1.75rem, 6vw, 2.125rem)/1.05 var(--ui-serif, "Instrument Serif", serif);
      }
      p.body { margin: 0; font-size: 13.5px; line-height: 1.6; color: var(--ink-mid, #545454); }
      .code {
        display: flex; justify-content: center; align-items: center;
        padding: clamp(16px, 5vw, 22px) 0;
        border: 1px solid var(--hr, rgba(10, 10, 10, 0.07)); border-radius: var(--ui-radius, 10px);
        background: var(--bg-1, #E9E6DE);
        font: 400 clamp(1.75rem, 8vw, 2.5rem)/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.1em; color: var(--ink-hi, #0A0A0A);
      }
      .status-row {
        display: flex; align-items: center; gap: 10px;
        padding: 12px 14px; border-radius: var(--ui-radius, 10px);
      }
      .status-row.wait { border: 1px dashed var(--hr-strong, rgba(10, 10, 10, 0.12)); }
      .status-row.confirmed { border: 1px solid var(--c-green, #36AA75); }
      .status-dot { width: 8px; height: 8px; border-radius: 999px; display: inline-block; flex-shrink: 0; }
      .status-row.wait .status-dot { background: var(--c-coral, #DF6662); animation: cnPulse 1.6s var(--ease-out, ease-out) infinite; }
      .status-row.confirmed .status-dot { background: var(--c-green, #36AA75); }
      .status-label {
        font: 400 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
      }
      .status-row.wait .status-label { color: var(--ink-mid, #545454); }
      .status-row.confirmed .status-label { color: light-dark(var(--c-green-deep, #2B7258), var(--c-green, #36AA75)); }
      @keyframes cnPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      .confirm, .restart {
        min-height: 2.5rem; border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px);
        padding: .6rem 1rem; font: 500 .875rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--bg-0, #FFFFFF); background: var(--ink-hi, #0A0A0A); cursor: pointer; width: 100%;
        transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out);
      }
      .confirm:hover, .restart:hover { background: var(--c-coral-deep, #B85551); border-color: var(--c-coral-deep, #B85551); }
      .report-mismatch {
        align-self: flex-start; background: none; border: none; padding: 0; cursor: pointer;
        font: 400 12.5px/1.4 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--ink-low, #888888); text-decoration: underline;
      }
      [role=alert] {
        margin: 0; padding: 13px 14px;
        border: 1px solid var(--c-coral, #DF6662); border-radius: var(--ui-radius, 10px);
        background: var(--gold-shd, rgba(223, 102, 98, 0.08));
        font-size: 12.5px; line-height: 1.55;
        color: light-dark(var(--c-coral-deep, #B85551), var(--c-coral, #DF6662));
      }
      .confirm:focus-visible, .restart:focus-visible, .report-mismatch:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      @media (prefers-reduced-motion: reduce) {
        * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation: none !important; }
      }
      @media (forced-colors: active) {
        section, .code, .status-row, .confirm, .restart, [role=alert] { border: 1px solid CanvasText; }
      }
    `;
        const section = document.createElement("section");
        const eyebrow = document.createElement("p");
        eyebrow.className = "eyebrow";
        eyebrow.textContent = "Secure";
        const heading = document.createElement("h1");
        const code = document.createElement("div");
        code.className = "code";
        code.setAttribute("role", "img");
        code.setAttribute("aria-label", `Match code: ${this.#state.code}`);
        code.textContent = this.#state.code;
        section.append(eyebrow, heading, code);
        const { status } = this.#state;
        if (status === "mismatch") {
            heading.textContent = "This code doesn’t match.";
            const alert = document.createElement("p");
            alert.setAttribute("role", "alert");
            alert.textContent =
                "The code shown here doesn’t match what the node is showing. This connection cannot continue " +
                    "— it may not be your node. Start over and try again.";
            section.append(alert);
            const restartButton = document.createElement("button");
            restartButton.type = "button";
            restartButton.className = "restart";
            restartButton.textContent = "Start over";
            restartButton.addEventListener("click", () => {
                this.dispatchEvent(new CustomEvent("restart", { bubbles: true, composed: true }));
            });
            section.append(restartButton);
        }
        else if (status === "confirmed") {
            heading.textContent = "Check the code on the node.";
            const statusRow = document.createElement("div");
            statusRow.className = "status-row confirmed";
            const dot = document.createElement("span");
            dot.className = "status-dot";
            const label = document.createElement("span");
            label.className = "status-label";
            label.textContent = "CONFIRMED ON THE NODE";
            statusRow.append(dot, label);
            section.append(statusRow);
        }
        else {
            heading.textContent = "Check the code on the node.";
            const body = document.createElement("p");
            body.className = "body";
            body.textContent =
                "The node is showing the same digits. Compare them, then confirm they match.";
            section.append(body);
            const statusRow = document.createElement("div");
            statusRow.className = "status-row wait";
            const dot = document.createElement("span");
            dot.className = "status-dot";
            const label = document.createElement("span");
            label.className = "status-label";
            label.textContent = "WAITING FOR THE NODE’S BUTTON";
            statusRow.append(dot, label);
            section.append(statusRow);
            const confirmButton = document.createElement("button");
            confirmButton.type = "button";
            confirmButton.className = "confirm";
            confirmButton.textContent = "Confirm they match";
            confirmButton.addEventListener("click", () => {
                this.dispatchEvent(new CustomEvent("confirm-matchcode", { bubbles: true, composed: true }));
            });
            section.append(confirmButton);
            const mismatchButton = document.createElement("button");
            mismatchButton.type = "button";
            mismatchButton.className = "report-mismatch";
            mismatchButton.textContent = "They don’t match";
            mismatchButton.addEventListener("click", () => {
                this.dispatchEvent(new CustomEvent("report-mismatch", { bubbles: true, composed: true }));
            });
            section.append(mismatchButton);
        }
        root.append(style, section);
    }
}
//# sourceMappingURL=secure-matchcode.js.map